import { useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  FileText,
  Calendar,
  AlertCircle,
  CheckCircle,
  XCircle,
} from "lucide-react";
import SEO from "../components/SEO";

const TermsOfService = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#0A0A0A] to-black">
      <SEO
        title="Syarat & Ketentuan | Putri Henna Artist"
        description="Syarat dan ketentuan layanan Putri Henna Artist. Informasi mengenai booking, pembayaran, hak pengguna, dan kebijakan penggunaan website."
        keywords="syarat ketentuan, terms of service, putri henna, booking henna, kebijakan layanan"
        url="https://tehputrihenna.com/terms"
      />
      {/* Decorative Elements */}
      <div className="absolute top-1/4 left-10 w-96 h-96 bg-[#D4AF7A]/5 rounded-full blur-3xl"></div>
      <div className="absolute bottom-1/4 right-10 w-96 h-96 bg-[#C9A882]/5 rounded-full blur-3xl"></div>

      <div className="relative max-w-4xl px-4 py-12 mx-auto sm:px-6 lg:px-8">
        {/* Back Button */}
        <button
          onClick={() => navigate("/")}
          className="inline-flex items-center gap-2 text-[#F7F5F2]/70 hover:text-[#D4AF7A] transition-colors mb-8"
        >
          <ArrowLeft size={20} />
          Kembali ke Beranda
        </button>

        {/* Header */}
        <div className="mb-12 text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-[#D4AF7A]/10 rounded-full mb-6">
            <FileText className="w-8 h-8 text-[#D4AF7A]" />
          </div>
          <h1 className="text-4xl sm:text-5xl font-bold text-[#F7F5F2] mb-4">
            Syarat & Ketentuan
          </h1>
          <p className="text-[#F7F5F2]/70 text-lg">
            Terakhir diperbarui:{" "}
            {new Date().toLocaleDateString("id-ID", {
              year: "numeric",
              month: "long",
              day: "numeric",
            })}
          </p>
        </div>

        {/* Content */}
        <div className="space-y-8">
          {/* Introduction */}
          <section className="bg-white/5 backdrop-blur-sm border border-[#D4AF7A]/20 rounded-2xl p-8">
            <p className="text-[#F7F5F2]/80 leading-relaxed">
              Selamat datang di website Putri Henna Artist. Syarat dan Ketentuan
              ini ("Terms") mengatur penggunaan website dan layanan yang
              disediakan oleh Putri Henna Artist. Dengan mengakses atau
              menggunakan website dan layanan kami, Anda setuju untuk terikat
              oleh Terms ini.
            </p>
          </section>

          {/* Acceptance of Terms */}
          <section className="bg-white/5 backdrop-blur-sm border border-[#D4AF7A]/20 rounded-2xl p-8">
            <div className="flex items-center gap-3 mb-6">
              <div className="p-3 rounded-lg bg-green-500/10">
                <CheckCircle className="w-6 h-6 text-green-400" />
              </div>
              <h2 className="text-2xl font-bold text-[#F7F5F2]">
                Penerimaan Terms
              </h2>
            </div>

            <p className="text-[#F7F5F2]/80 mb-4">
              Dengan mengakses dan menggunakan website ini, Anda menyatakan
              bahwa Anda:
            </p>

            <ul className="space-y-2 text-[#F7F5F2]/70">
              <li className="flex items-start gap-2">
                <span className="text-[#D4AF7A] mt-1">•</span>
                <span>Telah membaca, memahami, dan menyetujui Terms ini</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-[#D4AF7A] mt-1">•</span>
                <span>
                  Berusia minimal 18 tahun atau memiliki persetujuan orang tua
                </span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-[#D4AF7A] mt-1">•</span>
                <span>
                  Memiliki kapasitas hukum untuk terikat oleh Terms ini
                </span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-[#D4AF7A] mt-1">•</span>
                <span>Akan mematuhi semua hukum dan regulasi yang berlaku</span>
              </li>
            </ul>

            <div className="p-4 mt-6 border rounded-lg bg-yellow-500/10 border-yellow-500/30">
              <p className="text-sm text-yellow-400">
                <strong>Peringatan:</strong> Jika Anda tidak menyetujui Terms
                ini, jangan menggunakan website atau layanan kami.
              </p>
            </div>
          </section>

          {/* Services Description */}
          <section className="bg-white/5 backdrop-blur-sm border border-[#D4AF7A]/20 rounded-2xl p-8">
            <div className="flex items-center gap-3 mb-6">
              <div className="p-3 bg-[#D4AF7A]/10 rounded-lg">
                <FileText className="w-6 h-6 text-[#D4AF7A]" />
              </div>
              <h2 className="text-2xl font-bold text-[#F7F5F2]">
                Deskripsi Layanan
              </h2>
            </div>

            <p className="text-[#F7F5F2]/80 mb-4">
              Putri Henna Artist menyediakan layanan:
            </p>

            <ul className="space-y-3 text-[#F7F5F2]/70">
              <li className="flex items-start gap-3">
                <span className="text-[#D4AF7A]">✦</span>
                <span>
                  <strong>Henna Wedding</strong> - Layanan henna untuk
                  pernikahan dengan berbagai paket
                </span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-[#D4AF7A]">✦</span>
                <span>
                  <strong>Henna Engagement</strong> - Layanan henna untuk acara
                  lamaran
                </span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-[#D4AF7A]">✦</span>
                <span>
                  <strong>Henna Prewedding</strong> - Layanan henna untuk sesi
                  prewedding
                </span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-[#D4AF7A]">✦</span>
                <span>
                  <strong>Henna Graduation</strong> - Layanan henna untuk wisuda
                </span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-[#D4AF7A]">✦</span>
                <span>
                  <strong>Henna Pagar Ayu & Bridesmaid</strong> - Layanan henna
                  untuk pendamping pengantin
                </span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-[#D4AF7A]">✦</span>
                <span>
                  <strong>Fun Henna</strong> - Layanan henna untuk acara
                  keluarga dan fun events
                </span>
              </li>
            </ul>

            <p className="text-[#F7F5F2]/60 text-sm mt-4">
              Detail harga dan paket dapat dilihat di halaman Services website
              kami.
            </p>
          </section>

          {/* Booking and Payment */}
          <section className="bg-white/5 backdrop-blur-sm border border-[#D4AF7A]/20 rounded-2xl p-8">
            <div className="flex items-center gap-3 mb-6">
              <div className="p-3 bg-[#D4AF7A]/10 rounded-lg">
                <Calendar className="w-6 h-6 text-[#D4AF7A]" />
              </div>
              <h2 className="text-2xl font-bold text-[#F7F5F2]">
                Booking & Pembayaran
              </h2>
            </div>

            <div className="space-y-4">
              <div>
                <h3 className="text-lg font-semibold text-[#D4AF7A] mb-2">
                  Booking Tanggal
                </h3>
                <ul className="space-y-2 text-[#F7F5F2]/70">
                  <li className="flex items-start gap-2">
                    <span className="text-[#D4AF7A] mt-1">•</span>
                    <span>
                      Booking tanggal wajib melakukan DP (Down Payment)
                    </span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-[#D4AF7A] mt-1">•</span>
                    <span>
                      Untuk Henna Wedding/Prewedding/Engagement: DP sebesar Rp
                      100.000
                    </span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-[#D4AF7A] mt-1">•</span>
                    <span>
                      Untuk Henna Graduation/Fun Henna/Bridal Family/Pagar
                      Ayu/Bridesmaid: DP 40%
                    </span>
                  </li>
                </ul>
              </div>

              <div>
                <h3 className="text-lg font-semibold text-[#D4AF7A] mb-2">
                  Pelunasan
                </h3>
                <ul className="space-y-2 text-[#F7F5F2]/70">
                  <li className="flex items-start gap-2">
                    <span className="text-[#D4AF7A] mt-1">•</span>
                    <span>
                      Wedding / Pagar Ayu / Bridesmaid / Bridal Family: Maksimal
                      H-7
                    </span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-[#D4AF7A] mt-1">•</span>
                    <span>
                      Prewedding / Engagement / Graduation / Fun Henna: Maksimal
                      H-1
                    </span>
                  </li>
                </ul>
              </div>

              <div className="p-4 border rounded-lg bg-red-500/10 border-red-500/30">
                <p className="text-sm text-red-400">
                  <strong>Kebijakan Pembatalan:</strong>
                </p>
                <ul className="mt-2 space-y-1 text-sm text-red-400">
                  <li>• Batal 3-4 hari sebelum acara: DP dapat dikembalikan</li>
                  <li>• Batal kurang dari 3 hari sebelum acara: DP hangus</li>
                </ul>
              </div>
            </div>
          </section>

          {/* User Responsibilities */}
          <section className="bg-white/5 backdrop-blur-sm border border-[#D4AF7A]/20 rounded-2xl p-8">
            <div className="flex items-center gap-3 mb-6">
              <div className="p-3 rounded-lg bg-blue-500/10">
                <AlertCircle className="w-6 h-6 text-blue-400" />
              </div>
              <h2 className="text-2xl font-bold text-[#F7F5F2]">
                Tanggung Jawab Pengguna
              </h2>
            </div>

            <p className="text-[#F7F5F2]/80 mb-4">
              Sebagai pengguna website dan layanan kami, Anda setuju untuk:
            </p>

            <ul className="space-y-2 text-[#F7F5F2]/70">
              <li className="flex items-start gap-2">
                <span className="text-[#D4AF7A] mt-1">•</span>
                <span>
                  Memberikan informasi yang akurat, lengkap, dan terkini
                </span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-[#D4AF7A] mt-1">•</span>
                <span>Maintain kerahasiaan akun dan informasi login Anda</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-[#D4AF7A] mt-1">•</span>
                <span>
                  Tidak menggunakan website untuk tujuan ilegal atau tidak sah
                </span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-[#D4AF7A] mt-1">•</span>
                <span>
                  Tidak mengirimkan testimoni yang mengandung konten offensive,
                  spam, atau tidak pantas
                </span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-[#D4AF7A] mt-1">•</span>
                <span>Mematuhi semua hukum dan regulasi yang berlaku</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-[#D4AF7A] mt-1">•</span>
                <span>
                  Memahami bahwa testimoni yang dikirim akan direview sebelum
                  ditampilkan
                </span>
              </li>
            </ul>
          </section>

          {/* Intellectual Property */}
          <section className="bg-white/5 backdrop-blur-sm border border-[#D4AF7A]/20 rounded-2xl p-8">
            <div className="flex items-center gap-3 mb-6">
              <div className="p-3 rounded-lg bg-purple-500/10">
                <FileText className="w-6 h-6 text-purple-400" />
              </div>
              <h2 className="text-2xl font-bold text-[#F7F5F2]">
                Hak Kekayaan Intelektual
              </h2>
            </div>

            <p className="text-[#F7F5F2]/80 mb-4">
              Seluruh konten, desain, dan materi di website ini dilindungi oleh
              hak cipta dan hak kekayaan intelektual lainnya. Anda tidak
              diperbolehkan:
            </p>

            <ul className="space-y-2 text-[#F7F5F2]/70">
              <li className="flex items-start gap-2">
                <span className="mt-1 text-red-400">✗</span>
                <span>
                  Menyalin, memodifikasi, atau mendistribusikan konten tanpa
                  izin tertulis
                </span>
              </li>
              <li className="flex items-start gap-2">
                <span className="mt-1 text-red-400">✗</span>
                <span>
                  Menggunakan desain henna untuk tujuan komersial tanpa
                  persetujuan
                </span>
              </li>
              <li className="flex items-start gap-2">
                <span className="mt-1 text-red-400">✗</span>
                <span>
                  Mengklaim kepemilikan atas karya seni henna yang ditampilkan
                </span>
              </li>
              <li className="flex items-start gap-2">
                <span className="mt-1 text-green-400">✓</span>
                <span>
                  <strong>Boleh:</strong> Share foto henna di media sosial
                  dengan kredit kepada Putri Henna Artist
                </span>
              </li>
            </ul>
          </section>

          {/* Testimonials Policy */}
          <section className="bg-white/5 backdrop-blur-sm border border-[#D4AF7A]/20 rounded-2xl p-8">
            <div className="flex items-center gap-3 mb-6">
              <div className="p-3 bg-[#D4AF7A]/10 rounded-lg">
                <FileText className="w-6 h-6 text-[#D4AF7A]" />
              </div>
              <h2 className="text-2xl font-bold text-[#F7F5F2]">
                Kebijakan Testimoni
              </h2>
            </div>

            <p className="text-[#F7F5F2]/80 mb-4">
              Ketentuan untuk pengiriman testimoni:
            </p>

            <ul className="space-y-3 text-[#F7F5F2]/70">
              <li className="flex items-start gap-2">
                <span className="text-[#D4AF7A] mt-1">•</span>
                <span>
                  <strong>Review Process:</strong> Semua testimoni akan direview
                  oleh admin sebelum ditampilkan
                </span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-[#D4AF7A] mt-1">•</span>
                <span>
                  <strong>Approval:</strong> Testimoni yang mengandung konten
                  offensive, spam, atau tidak relevan dapat ditolak
                </span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-[#D4AF7A] mt-1">•</span>
                <span>
                  <strong>Keaslian:</strong> Testimoni harus berdasarkan
                  pengalaman nyata dengan layanan kami
                </span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-[#D4AF7A] mt-1">•</span>
                <span>
                  <strong>Hak Edit:</strong> Kami berhak mengedit atau
                  memodifikasi testimoni untuk tujuan tampilan (tanpa mengubah
                  makna)
                </span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-[#D4AF7A] mt-1">•</span>
                <span>
                  <strong>Publikasi:</strong> Testimoni yang disetujui akan
                  ditampilkan di website dan media sosial kami
                </span>
              </li>
            </ul>
          </section>

          {/* Limitation of Liability */}
          <section className="bg-white/5 backdrop-blur-sm border border-[#D4AF7A]/20 rounded-2xl p-8">
            <div className="flex items-center gap-3 mb-6">
              <div className="p-3 rounded-lg bg-red-500/10">
                <XCircle className="w-6 h-6 text-red-400" />
              </div>
              <h2 className="text-2xl font-bold text-[#F7F5F2]">
                Batasan Tanggung Jawab
              </h2>
            </div>

            <p className="text-[#F7F5F2]/80 mb-4">
              Sejauh diizinkan oleh hukum:
            </p>

            <ul className="space-y-2 text-[#F7F5F2]/70">
              <li className="flex items-start gap-2">
                <span className="text-[#D4AF7A] mt-1">•</span>
                <span>
                  Kami tidak bertanggung jawab atas kerusakan langsung, tidak
                  langsung, insidental, atau konsekuensial
                </span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-[#D4AF7A] mt-1">•</span>
                <span>
                  Kami tidak menjamin bahwa website akan selalu tersedia, aman,
                  atau bebas error
                </span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-[#D4AF7A] mt-1">•</span>
                <span>
                  Kami tidak bertanggung jawab atas konten pihak ketiga yang
                  di-link dari website kami
                </span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-[#D4AF7A] mt-1">•</span>
                <span>
                  Hasil henna dapat bervariasi tergantung kondisi kulit dan
                  perawatan setelah aplikasi
                </span>
              </li>
            </ul>

            <div className="mt-4 p-4 bg-[#D4AF7A]/5 border border-[#D4AF7A]/20 rounded-lg">
              <p className="text-[#F7F5F2]/70 text-sm">
                <strong>Catatan:</strong> Kami akan berusaha memberikan layanan
                terbaik, namun hasil akhir dapat dipengaruhi oleh berbagai
                faktor di luar kontrol kami.
              </p>
            </div>
          </section>

          {/* Termination */}
          <section className="bg-white/5 backdrop-blur-sm border border-[#D4AF7A]/20 rounded-2xl p-8">
            <div className="flex items-center gap-3 mb-6">
              <div className="p-3 rounded-lg bg-orange-500/10">
                <XCircle className="w-6 h-6 text-orange-400" />
              </div>
              <h2 className="text-2xl font-bold text-[#F7F5F2]">
                Penghentian Layanan
              </h2>
            </div>

            <p className="text-[#F7F5F2]/80 mb-4">Kami berhak untuk:</p>

            <ul className="space-y-2 text-[#F7F5F2]/70">
              <li className="flex items-start gap-2">
                <span className="text-[#D4AF7A] mt-1">•</span>
                <span>
                  Membatasi atau menangguhkan akses Anda ke website dengan atau
                  tanpa alasan
                </span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-[#D4AF7A] mt-1">•</span>
                <span>
                  Menghapus atau memodifikasi konten website setiap saat
                </span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-[#D4AF7A] mt-1">•</span>
                <span>Menghentikan layanan jika Terms dilanggar</span>
              </li>
            </ul>
          </section>

          {/* Changes to Terms */}
          <section className="bg-white/5 backdrop-blur-sm border border-[#D4AF7A]/20 rounded-2xl p-8">
            <div className="flex items-center gap-3 mb-6">
              <div className="p-3 bg-[#D4AF7A]/10 rounded-lg">
                <AlertCircle className="w-6 h-6 text-[#D4AF7A]" />
              </div>
              <h2 className="text-2xl font-bold text-[#F7F5F2]">
                Perubahan Terms
              </h2>
            </div>

            <p className="text-[#F7F5F2]/80">
              Kami berhak mengubah Terms ini kapan saja. Perubahan akan efektif
              segera setelah diposting di website. Penggunaan Anda yang
              berkelanjutan constitutes acceptance dari Terms yang telah diubah.
            </p>
          </section>

          {/* Governing Law */}
          <section className="bg-white/5 backdrop-blur-sm border border-[#D4AF7A]/20 rounded-2xl p-8">
            <h2 className="text-xl font-bold text-[#F7F5F2] mb-4">
              Hukum yang Berlaku
            </h2>
            <p className="text-[#F7F5F2]/80">
              Terms ini diatur oleh hukum Republik Indonesia. Setiap
              perselisihan akan diselesaikan melalui negosiasi terlebih dahulu.
              Jika tidak mencapai kesepakatan, perselisihan akan diselesaikan
              melalui pengadilan yang berwenang di Indonesia.
            </p>
          </section>

          {/* Contact Information */}
          <section className="bg-gradient-to-r from-[#C9A882]/10 to-[#D4AF7A]/10 backdrop-blur-sm border border-[#D4AF7A]/30 rounded-2xl p-8">
            <h2 className="text-xl font-bold text-[#F7F5F2] mb-4">
              Hubungi Kami
            </h2>
            <p className="text-[#F7F5F2]/80 mb-4">
              Jika Anda memiliki pertanyaan tentang Terms ini, silakan hubungi
              kami:
            </p>
            <div className="space-y-2 text-[#F7F5F2]/70">
              <p>
                <strong className="text-[#D4AF7A]">Email:</strong>{" "}
                putrileony.office@gmail.com
              </p>
              <p>
                <strong className="text-[#D4AF7A]">Website:</strong>{" "}
                tehputrihenna.com
              </p>
            </div>
          </section>
        </div>

        {/* Footer Note */}
        <div className="mt-12 text-center text-sm text-[#F7F5F2]/50">
          <p>
            Terima kasih telah mempercayakan Putri Henna Artist untuk momen
            spesial Anda.
          </p>
          <p className="mt-2">
            Terms ini terakhir diperbarui pada{" "}
            {new Date().toLocaleDateString("id-ID", {
              year: "numeric",
              month: "long",
              day: "numeric",
            })}
          </p>
        </div>
      </div>
    </div>
  );
};

export default TermsOfService;
